import Swiper from "swiper";
import { A11y, Autoplay, Pagination } from "swiper/modules";
import { isTouchDevice } from "@/scripts/common/util";
import { gsap } from "gsap";

document.querySelectorAll(".card-slider").forEach((el) => {
  const container = el.querySelector(".swiper");
  const slides = el.querySelectorAll(".swiper-slide");
  const sliderPagination = el.querySelector(".slider-pagination");
  const expandFactor = 2;

  let activeSlide = null;
  let activeSlidePos = { x: 0, y: 0 };
  let activeSlideStartTime = 0;

  let mouseX = 0;
  let mouseY = 0;

  const MOUSE_BUFFER = 40;

  window.addEventListener("mousemove", (e) => {
    mouseX = e.clientX;
    mouseY = e.clientY;
  });

  const swiper = new Swiper(container, {
    modules: [A11y, Autoplay, Pagination],
    loop: true,
    autoplay: {
      delay: 4000,
      pauseOnMouseEnter: true,
    },
    slidesPerView: 1,
    spaceBetween: 16,
    speed: 800,
    slideToClickedSlide: true,
    pagination: {
      el: sliderPagination,
      clickable: true,
      renderBullet: (index, className) => {
        return `
          <span class="${className}" style="flex: 1; height: 100%; position: relative; overflow: hidden;">
            <span class="position-bullet" style="position: absolute; top: 50%; right: -11px; transform: translate(-50%, -50%); width: var(--spacing-slider-bullet-size); height: var(--spacing-slider-bullet-size); background-color: #FFF; border-radius: 50%; z-index: 10;"></span>
            <span class="progress-track" style="position: absolute; top: 0; left: 0; height: 100%; width: 0; background-color: var(--color-highlight, #A7AF39); z-index: 1;"></span>
          </span>`;
      },
    },
    breakpoints: {
      768: {
        spaceBetween: 16,
      },
      1024: {
        spaceBetween: 0,
        slidesPerView: 3,
      },
      1440: {
        spaceBetween: 0,
        slidesPerView: 4,
      },
      1920: {
        spaceBetween: 0,
        slidesPerView: 5,
      }
    },
  });

  if (sliderPagination) {
    let currentSlideIndex = 0;

    swiper.on("slideChange", function() {
      const bullets = Array.from(sliderPagination.querySelectorAll('.swiper-pagination-bullet'));
      const totalBullets = bullets.length;
      const realIndex = swiper.realIndex;

      if (swiper.previousIndex > realIndex && realIndex === 0) {
        currentSlideIndex = 0;
        bullets.forEach(bullet => {
          bullet.classList.remove('completed');
          const progressTrack = bullet.querySelector('.progress-track');
          if (progressTrack) progressTrack.style.width = '0';
        });
      } else if (swiper.previousIndex < swiper.realIndex ||
              (swiper.previousIndex === totalBullets - 1 && swiper.realIndex === 0)) {
        currentSlideIndex = Math.max(currentSlideIndex, realIndex);
      }

      bullets.forEach((bullet, index) => {
        const progressTrack = bullet.querySelector('.progress-track');

        if (index < realIndex) {
          bullet.classList.add('completed');
          if (progressTrack) progressTrack.style.width = '100%';
        } else if (index === realIndex) {
          bullet.classList.remove('completed');
        } else {
          bullet.classList.remove('completed');
          if (progressTrack) progressTrack.style.width = '0';
        }
      });
    });
  }

  swiper.autoplay.stop();

  const observer = new IntersectionObserver(
    ([e]) => {
      if (e.isIntersecting) {
        swiper.autoplay.start();
        observer.disconnect();
      }
    },
    {
      threshold: [0.7],
    },
  );

  observer.observe(el);

  if (window.matchMedia("(min-width: 1024px)").matches && !isTouchDevice) {
    const settleContainer = (activeSlide) => {
      gsap.to(container, {
        x: () => {
          if (activeSlide === null) return 0;

          const rect = activeSlide.getBoundingClientRect();
          const currentX = gsap.getProperty(container, "x") || 0;
          const naturalLeft = rect.x - currentX;
          const slideWidth = rect.width;

          // Center the slide in the viewport
          let targetX = window.innerWidth / 2 - (naturalLeft + slideWidth / 2);

          // Prevent the edges from crossing the mouse pointer to avoid card conflicts
          const maxT = mouseX - naturalLeft - MOUSE_BUFFER;
          const minT = mouseX - naturalLeft - slideWidth + MOUSE_BUFFER;

          targetX = Math.max(minT, Math.min(maxT, targetX));

          return targetX;
        },
        duration: 1.2,
        ease: "power2.out",
      });
    };

    slides.forEach((slide) => {
      const overlay = slide.querySelector(".item-overlay");
      const screen = slide.querySelector(".item-screen");
      const image = slide.querySelector(".item-image");

      let enterTimeout;
      let leaveTimeout;
      let isAnimating = false;
      let isExpanded = false;

      const timeline = gsap.timeline({
        paused: true,
        onStart: () => {
          isAnimating = true;
        },
        onComplete: () => {
          isAnimating = false;
          isExpanded = true;
          settleContainer(activeSlide);
        },
        onReverseComplete: () => {
          isAnimating = false;
          isExpanded = false;
          slide.style.height = null;
          if (activeSlide === slide) {
            activeSlide = null;
          }
          settleContainer(activeSlide);
        },
      });

      timeline.to(overlay, { opacity: 0, duration: 0.3, ease: "power2.out" });

      timeline.to(slide, { width: `${parseFloat(slide.offsetWidth) * expandFactor}px`, duration: 0.5, ease: "power2.out" }, "<30%");
      timeline.to(
        image,
        {
          width: image.offsetWidth,
          duration: 0.2,
          ease: "power2.out",
        },
        "<",
      );
      timeline.fromTo(
        screen,
        { opacity: 0, visibility: "invisible" },
        {
          opacity: 1,
          visibility: "visible",
          pointerEvents: "auto",
          duration: 0.3,
          ease: "power2.out",
        },
        "<20%",
      );
      timeline.fromTo(
        Array.from(screen.querySelector(".item-screen-children").children),
        { opacity: 0, y: "15%" },
        { opacity: 1, duration: 0.4, y: 0, stagger: 0.12, ease: "power2.out" },
      );

      slide.addEventListener("mouseenter", (e) => {
        const mouseX = e.clientX;
        const mouseY = e.clientY;

        clearTimeout(leaveTimeout);

        if (isExpanded || (isAnimating && activeSlide === slide)) return;

        if (activeSlide && activeSlide !== slide) {
          const dist = Math.sqrt(
            Math.pow(mouseX - activeSlidePos.x, 2) + Math.pow(mouseY - activeSlidePos.y, 2)
          );
          const timeSince = Date.now() - activeSlideStartTime;
          if (dist < 10 && timeSince < 300) return;

          slides.forEach((otherSlide) => {
            if (otherSlide !== slide) {
              const otherTimeline = otherSlide._timeline;
              if (
                otherTimeline &&
                (otherTimeline.isActive() || otherTimeline.progress() > 0)
              ) {
                clearTimeout(otherSlide._enterTimeout);
                clearTimeout(otherSlide._leaveTimeout);
                otherTimeline.timeScale(2).reverse();
              }
            }
          });
        }

        clearTimeout(enterTimeout);
        enterTimeout = setTimeout(() => {
          activeSlide = slide;
          activeSlidePos = { x: mouseX, y: mouseY };
          activeSlideStartTime = Date.now();
          slide.style.height = `${slide.dataset.height}px`;
          timeline.timeScale(1).play();
        }, 300);
      });

      slide.addEventListener("mouseleave", () => {
        clearTimeout(enterTimeout);

        if (!isExpanded && !isAnimating) return;

        clearTimeout(leaveTimeout);
        leaveTimeout = setTimeout(() => {
          if (activeSlide === slide || isExpanded) {
            timeline.timeScale(1.4).reverse();
          }
        }, 200);
      });

      slide._timeline = timeline;
      slide._enterTimeout = enterTimeout;
      slide._leaveTimeout = leaveTimeout;
    });
  }
});

const observer = new ResizeObserver(() => {
  document.querySelectorAll(".card-slider .swiper-slide").forEach((slide) => {
    slide.setAttribute("data-height", slide.offsetHeight);
  });
});

observer.observe(document.body);
